import React, { useRef, useState } from 'react';
import { Btn, C, Input, Select } from '../../../components/UI';
import { platformApi, MarketplaceAppAdminDto, CreateMarketplaceAppRequest, AppFeatureItemDto } from '../../../services/api';

const sizeNote: React.CSSProperties = { fontSize: 11, color: C.textMuted, marginTop: 5 };

const fieldLabel: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif", resize: 'vertical' };

// Features are edited as "Title | Description" lines rather than a repeating-row builder —
// pragmatic for a first-party-only catalog authored by our own team, not tenant-facing.
function featuresToText(items: AppFeatureItemDto[]): string { return items.map(f => `${f.title} | ${f.description}`).join('\n'); }
function textToFeatures(text: string): AppFeatureItemDto[] {
    return text.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
        const [title, ...rest] = line.split('|');
        return { title: title.trim(), description: rest.join('|').trim() };
    });
}
function listToText(items: string[]): string { return items.join('\n'); }
function textToList(text: string): string[] { return text.split('\n').map(l => l.trim()).filter(Boolean); }

export default function MarketplaceAppEditForm({ app, onSaved, onCancel }: {
    app: MarketplaceAppAdminDto | null;
    onSaved: () => void;
    onCancel: () => void;
}) {
    const isNew = app === null;
    const [key, setKey] = useState(app?.key ?? '');
    const [name, setName] = useState(app?.name ?? '');
    const [shortDescription, setShortDescription] = useState(app?.shortDescription ?? '');
    const [longDescriptionHtml, setLongDescriptionHtml] = useState(app?.longDescriptionHtml ?? '');
    const [logoUrl, setLogoUrl] = useState(app?.logoUrl ?? '');
    const [category, setCategory] = useState(app?.category ?? 'Other');
    const [isFeatured, setIsFeatured] = useState(app?.isFeatured ?? false);
    const [sortOrder, setSortOrder] = useState(String(app?.sortOrder ?? 0));
    const [pricingModel, setPricingModel] = useState<'Free' | 'Paid'>(app?.pricingModel ?? 'Free');
    const [monthlyPrice, setMonthlyPrice] = useState(app?.monthlyPrice != null ? String(app.monthlyPrice) : '');
    const [stripeProductId, setStripeProductId] = useState(app?.stripeProductId ?? '');
    const [stripePriceId, setStripePriceId] = useState(app?.stripePriceId ?? '');
    const [setupType, setSetupType] = useState<'Toggle' | 'OAuthConnect' | 'ApiKeyEntry'>(app?.setupType ?? 'Toggle');
    const [setupConfigJson, setSetupConfigJson] = useState(app?.setupConfigJson ?? '');
    const [featuresText, setFeaturesText] = useState(featuresToText(app?.features ?? []));
    const [prerequisitesText, setPrerequisitesText] = useState(listToText(app?.prerequisites ?? []));
    const [installStepsText, setInstallStepsText] = useState(listToText(app?.installSteps ?? []));
    const [screenshotUrlsText, setScreenshotUrlsText] = useState(listToText(app?.screenshotUrls ?? []));
    const [developerName, setDeveloperName] = useState(app?.developerName ?? 'ServiceTrek');
    const [supportUrl, setSupportUrl] = useState(app?.supportUrl ?? '');
    const [supportEmail, setSupportEmail] = useState(app?.supportEmail ?? '');

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [logoUploading, setLogoUploading] = useState(false);
    const logoFileRef = useRef<HTMLInputElement>(null);
    const uploadLogo = async (file: File) => {
        if (isNew) return;
        setLogoUploading(true);
        setError('');
        try {
            const { logoUrl: url } = await platformApi.uploadMarketplaceAppLogo(app!.id, file);
            setLogoUrl(url);
        } catch (e: any) {
            setError(e.message || 'Failed to upload logo.');
        } finally {
            setLogoUploading(false);
        }
    };

    const [screenshotUploading, setScreenshotUploading] = useState(false);
    const screenshotFileRef = useRef<HTMLInputElement>(null);
    const uploadScreenshot = async (file: File) => {
        if (isNew) return;
        setScreenshotUploading(true);
        setError('');
        try {
            const { screenshotUrls } = await platformApi.uploadMarketplaceAppScreenshot(app!.id, file);
            setScreenshotUrlsText(listToText(screenshotUrls));
        } catch (e: any) {
            setError(e.message || 'Failed to upload screenshot.');
        } finally {
            setScreenshotUploading(false);
        }
    };

    const save = async () => {
        setSaving(true);
        setError('');
        const payload: CreateMarketplaceAppRequest = {
            key: key.trim(),
            name: name.trim(),
            shortDescription: shortDescription.trim(),
            longDescriptionHtml,
            logoUrl: logoUrl.trim() || null,
            category: category.trim() || 'Other',
            isFeatured,
            sortOrder: parseInt(sortOrder, 10) || 0,
            pricingModel,
            monthlyPrice: pricingModel === 'Paid' ? (parseFloat(monthlyPrice) || 0) : null,
            stripeProductId: pricingModel === 'Paid' ? stripeProductId.trim() || null : null,
            stripePriceId: pricingModel === 'Paid' ? stripePriceId.trim() || null : null,
            setupType,
            setupConfigJson: setupConfigJson.trim() || null,
            features: textToFeatures(featuresText),
            prerequisites: textToList(prerequisitesText),
            installSteps: textToList(installStepsText),
            screenshotUrls: textToList(screenshotUrlsText),
            developerName: developerName.trim() || 'ServiceTrek',
            supportUrl: supportUrl.trim() || null,
            supportEmail: supportEmail.trim() || null,
        };

        try {
            if (isNew) await platformApi.createMarketplaceApp(payload);
            else await platformApi.updateMarketplaceApp(app!.id, payload);
            onSaved();
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <button onClick={onCancel} style={{ background: 'none', border: 'none', color: C.textSub, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 16 }}>
                ← App Marketplace catalog
            </button>
            <h1 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700, color: C.text }}>{isNew ? 'New app' : `Edit ${app!.name}`}</h1>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 4 }}>
                <Input label={'Key (stable slug, e.g. "quickbooks")'} value={key} onChange={setKey} placeholder="my-app" required />
                <Input label="Name" value={name} onChange={setName} required />
            </div>

            <Input label="Short description (shown on the catalog card)" value={shortDescription} onChange={setShortDescription} required />

            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabel}>Long description (HTML, shown on the detail page)</label>
                <textarea value={longDescriptionHtml} onChange={e => setLongDescriptionHtml(e.target.value)} rows={4} style={textareaStyle} />
            </div>

            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabel}>Logo</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {logoUrl && (
                        <div style={{ width: 44, height: 44, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: C.bg, border: `1px solid ${C.border}` }}>
                            <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                    )}
                    <input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://..."
                        style={{ flex: 1, padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                    <Btn variant="outline" size="sm" onClick={() => logoFileRef.current?.click()} disabled={isNew || logoUploading}>
                        {logoUploading ? '...' : 'Upload'}
                    </Btn>
                    <input ref={logoFileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) uploadLogo(f); }} />
                </div>
                <div style={sizeNote}>
                    {isNew
                        ? 'Save the app first, then reopen it to upload a logo.'
                        : 'Square image, at least 256×256px recommended (shown as small as 36px, so extra resolution keeps it sharp on retina screens). PNG, JPG, SVG, or WebP.'}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Input label="Category" value={category} onChange={setCategory} placeholder="Accounting" />
                <Input label="Sort order" value={sortOrder} onChange={setSortOrder} type="number" />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <input type="checkbox" checked={isFeatured} onChange={e => setIsFeatured(e.target.checked)} id="featured" />
                <label htmlFor="featured" style={{ fontSize: 13, color: C.textSub }}>Featured on the marketplace browse page</label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Select label="Pricing" value={pricingModel} onChange={v => setPricingModel(v as any)} options={[{ value: 'Free', label: 'Free' }, { value: 'Paid', label: 'Paid' }]} />
                <Select label="Setup type" value={setupType} onChange={v => setSetupType(v as any)} options={[
                    { value: 'Toggle', label: 'Toggle (simple install)' },
                    { value: 'OAuthConnect', label: 'OAuth connect' },
                    { value: 'ApiKeyEntry', label: 'API key entry' },
                ]} />
            </div>

            {pricingModel === 'Paid' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                    <Input label="Monthly price ($)" value={monthlyPrice} onChange={setMonthlyPrice} type="number" required />
                    <Input label="Stripe Product ID" value={stripeProductId} onChange={setStripeProductId} placeholder="prod_..." required />
                    <Input label="Stripe Price ID" value={stripePriceId} onChange={setStripePriceId} placeholder="price_..." required />
                </div>
            )}

            {setupType !== 'Toggle' && (
                <div style={{ marginBottom: 14 }}>
                    <label style={fieldLabel}>
                        Setup config JSON — {setupType === 'OAuthConnect'
                            ? 'e.g. {"connectEndpointKey":"quickbooks"}'
                            : 'e.g. [{"key":"apiKey","label":"API Key","secret":true}]'}
                    </label>
                    <textarea value={setupConfigJson} onChange={e => setSetupConfigJson(e.target.value)} rows={2} style={{ ...textareaStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }} />
                </div>
            )}

            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabel}>Features & benefits — one per line, "Title | Description"</label>
                <textarea value={featuresText} onChange={e => setFeaturesText(e.target.value)} rows={3} style={textareaStyle} placeholder={'Live sync | Automatically syncs job payments to your books'} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ marginBottom: 14 }}>
                    <label style={fieldLabel}>Before you start — one per line</label>
                    <textarea value={prerequisitesText} onChange={e => setPrerequisitesText(e.target.value)} rows={3} style={textareaStyle} />
                </div>
                <div style={{ marginBottom: 14 }}>
                    <label style={fieldLabel}>Installation steps — one per line</label>
                    <textarea value={installStepsText} onChange={e => setInstallStepsText(e.target.value)} rows={3} style={textareaStyle} />
                </div>
            </div>

            <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                    <label style={{ ...fieldLabel, marginBottom: 0 }}>Gallery screenshots — one URL per line, or upload below</label>
                    <Btn variant="outline" size="sm" onClick={() => screenshotFileRef.current?.click()} disabled={isNew || screenshotUploading}>
                        {screenshotUploading ? 'Uploading…' : '+ Upload screenshot'}
                    </Btn>
                    <input ref={screenshotFileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) uploadScreenshot(f); e.target.value = ''; }} />
                </div>
                <textarea value={screenshotUrlsText} onChange={e => setScreenshotUrlsText(e.target.value)} rows={3} style={textareaStyle} />
                <div style={sizeNote}>
                    {isNew
                        ? 'Save the app first, then reopen it to upload screenshots.'
                        : 'Displayed at 140px tall, original aspect ratio kept (not cropped) — landscape images around 1200×750px work well. PNG, JPG, or WebP. Uploading adds to this list; delete a line to remove one.'}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <Input label="Developer name" value={developerName} onChange={setDeveloperName} />
                <Input label="Support email" value={supportEmail} onChange={setSupportEmail} />
                <Input label="Support URL" value={supportUrl} onChange={setSupportUrl} />
            </div>

            {error && <div style={{ color: '#D84040', fontSize: 13, marginBottom: 14 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} disabled={saving || !key.trim() || !name.trim() || !shortDescription.trim()}>
                    {saving ? 'Saving…' : isNew ? 'Create app' : 'Save changes'}
                </Btn>
            </div>
        </div>
    );
}
